import type { PageEntry, Node } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import htmlParser from 'html-react-parser';
import dynamic from 'next/dynamic';

import type { ButtonIconVariants } from 'Theme/types/elements.type';

import { H3 } from 'Elements/Typography';

import type { Props } from './International';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors } = data;

    const {
        title,
        sys,
        heroBanner,
        // Welcome
        introHeading,
        introText,
        introCTAs,
        // Section nav
        sectionNavigationHeading,
        iconSectionNavigation,
        // Not started
        notStartedHeading,
        notStartedSupportInformation,
        lifeInSouthamptonHeading,
        lifeInSouthamptonDescription,
        lifeInSouthamptonLink,
        lifeInSouthamptonVideo,
        lifeInSouthamptonImage,
        // Applied
        appliedHeading,
        appliedSupportInformation,
        findYourCountry,
        // Aplication confirmed
        applicationConfirmedHeading,
        applicationConfirmedSupportInformation,
        // On my way
        onMyWayHeading,
        onMyWaySupportInformation,
        // Events
        events,
        // Unibuddy metadata
        unibuddyMetadata
    } = entry;

    const buttonIconVariants: ButtonIconVariants[] = [
        'accentFiveSolid',
        'accentNineSolid',
        'accentTenSolid'
    ];

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            ...cmsParser.heroBannerWave(heroBanner),
            oneColumn: true
        },
        welcomeSection: {
            heading: introHeading,
            text: htmlParser(introText),
            buttons: introCTAs.map((cta, index) => ({
                ...cmsParser.linkButton(cta),
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(icons => icons[cta.icon.name])
                ),
                variant: buttonIconVariants[index]
            }))
        },
        sectionNavigation: {
            heading: sectionNavigationHeading,
            links: iconSectionNavigation.map(item => ({
                link: {
                    label: item.label,
                    ariaLabel: `Scroll to ${item.label}`,
                    href: `#${item.label.toLowerCase().split(' ').join('-')}`
                },
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(icons => icons[item.icon.name])
                )
            }))
        },
        informationGridOne: {
            heading: notStartedHeading,
            grid: {
                items: notStartedSupportInformation.map(item => ({
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    ),
                    heading: item.heading,
                    headingType: 'h3',
                    text: item.description,
                    link: cmsParser.link(item.link)
                }))
            }
        },
        lifeSection: {
            heading: lifeInSouthamptonHeading,
            text: htmlParser(lifeInSouthamptonDescription),
            link: lifeInSouthamptonLink ? cmsParser.arrowLink(lifeInSouthamptonLink) : undefined,
            video: lifeInSouthamptonVideo
                ? cmsParser.transcriptEmbed(lifeInSouthamptonVideo)
                : undefined,
            image: cmsParser.image(lifeInSouthamptonImage)
        },
        informationGridTwo: {
            heading: appliedHeading,
            grid: {
                items: appliedSupportInformation.map(item => ({
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    ),
                    heading: item.heading,
                    headingType: 'h3',
                    text: item.description,
                    link: cmsParser.link(item.link)
                }))
            }
        },
        countries: {
            heading: findYourCountry.heading,
            text: htmlParser(findYourCountry.bodyText),
            link: findYourCountry.linkCTA.label
                ? cmsParser.link(findYourCountry.linkCTA)
                : undefined,
            order: findYourCountry.layout === 'Text on right, accordion on left' ? '1' : 'initial',
            accordions: findYourCountry.accordion
                .filter(item => item.content)
                .map(item => ({
                    id: item.title,
                    heading: (
                        <H3
                            size={{ small: '1.25rem', large: '1.5rem' }}
                            spacing={{ small: { margin: '0' } }}
                        >
                            {item.title}
                        </H3>
                    ),
                    button: {
                        ariaLabel: `View ${item.title}`
                    },
                    variant: 'tertiary',
                    children: htmlParser(item.content)
                }))
        },
        informationGridThree: {
            heading: applicationConfirmedHeading,
            grid: {
                items: applicationConfirmedSupportInformation.map(item => ({
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    ),
                    heading: item.heading,
                    headingType: 'h3',
                    text: item.description,
                    link: cmsParser.link(item.link)
                }))
            }
        },
        informationGridFour: {
            heading: onMyWayHeading,
            grid: {
                items: onMyWaySupportInformation.map(item => ({
                    icon: dynamic(() =>
                        import('@solent-university/solent-icons').then(
                            icons => icons[item.icon.name]
                        )
                    ),
                    heading: item.heading,
                    headingType: 'h3',
                    text: item.description,
                    link: cmsParser.link(item.link)
                }))
            }
        },
        brandBanner: {
            heading: events.heading,
            text: events.description,
            link: {
                ...cmsParser.linkButton(events.buttonCTA),
                variant: events.buttonVariant ? events.buttonVariant : 'primary'
            },
            backgroundColor: cmsParser.getThemeColour(events.backgroundColor),
            textColor: cmsParser.getThemeColour(events.textColor)
        },
        uniBuddyPopcard: {
            hide: unibuddyMetadata?.hideUnibuddy,
            filterValue: unibuddyMetadata?.courseLevelFilter
        }
    };
};

export default datamap;
